import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { PDFViewer, PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { Invoice, InvoiceItem } from '../../types/invoice';
import { BusinessData } from '../../types/business';
import { InvoiceTemplateInterface, InvoiceTemplateType } from '../../utils/invoiceTemplates'; // Updated import
import {
  CircularProgress,
  Box,
  Button,
  Alert,
  Typography,
  Paper,
  Tooltip,
  IconButton,
  Skeleton,
} from '@mui/material';
import {
  DownloadOutlined as DownloadIcon,
  PrintOutlined as PrintIcon,
  EmailOutlined as EmailIcon,
  ShareOutlined as ShareIcon,
} from '@mui/icons-material';

// Register custom fonts
try {
  Font.register({
    family: 'Poppins',
    fonts: [
      { src: 'https://cdn.jsdelivr.net/npm/@fontsource/poppins@4.5.0/files/poppins-latin-400-normal.woff', fontWeight: 400 },
      { src: 'https://cdn.jsdelivr.net/npm/@fontsource/poppins@4.5.0/files/poppins-latin-700-normal.woff', fontWeight: 700 },
    ],
  });

  Font.register({
    family: 'Roboto',
    fonts: [
      { src: 'https://cdn.jsdelivr.net/npm/@fontsource/roboto@4.5.0/files/roboto-latin-400-normal.woff', fontWeight: 400 },
      { src: 'https://cdn.jsdelivr.net/npm/@fontsource/roboto@4.5.0/files/roboto-latin-700-normal.woff', fontWeight: 700 },
    ],
  });
  // Additional font for Futuristic if needed, using Roboto for now to ensure reliability
} catch (error) {
  console.error('Error registering fonts:', error);
}

// Helper to create styles based on template
const getThemeStyles = (template: InvoiceTemplateInterface) => {
  const primary = template.colorScheme?.primary || '#000000';
  const secondary = template.colorScheme?.secondary || '#555555';
  const background = template.colorScheme?.background || '#FFFFFF';
  const text = template.colorScheme?.text || '#000000';
  const font = template.fontFamily || 'Helvetica';
  const isDark = template.variant === 'futuristic';

  return StyleSheet.create({
    page: {
      padding: 30,
      fontFamily: font,
      backgroundColor: background,
      color: text,
    },
    // Header Styles
    headerContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 30,
      borderBottomWidth: template.variant === 'formal' ? 2 : 0,
      borderBottomColor: primary,
      paddingBottom: template.variant === 'formal' ? 10 : 0,
    },
    logo: {
      width: 120,
      height: 'auto',
      marginBottom: 10,
    },
    businessTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: primary,
      textTransform: template.variant === 'futuristic' ? 'uppercase' : 'none',
    },
    invoiceTitleBlock: {
      alignItems: 'flex-end',
    },
    invoiceTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: isDark ? '#FFFFFF' : primary,
      letterSpacing: isDark ? 2 : 0,
    },

    // Metadata (Invoice #, Date)
    metaContainer: {
      marginTop: 10,
    },
    metaRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      marginBottom: 4,
    },
    metaLabel: {
      fontSize: 10,
      color: secondary,
      marginRight: 8,
      fontWeight: 'bold',
    },
    metaValue: {
      fontSize: 10,
      color: text,
    },

    // Address Section
    addressSection: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 30,
      marginTop: 10,
    },
    addressBlock: {
      width: '45%',
    },
    addressLabel: {
      fontSize: 10,
      color: secondary,
      fontWeight: 'bold',
      marginBottom: 4,
      textTransform: 'uppercase',
      borderBottomWidth: 1,
      borderBottomColor: '#EEE',
      paddingBottom: 2,
    },
    addressText: {
      fontSize: 10,
      marginBottom: 3,
      lineHeight: 1.4,
    },

    // Table
    tableContainer: {
      marginTop: 10,
      marginBottom: 20,
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: isDark ? '#1e293b' : (template.variant === 'modern' ? primary : '#F5F5F5'),
      padding: 8,
      borderBottomWidth: 1,
      borderBottomColor: primary,
    },
    tableHeaderCell: {
      fontSize: 10,
      fontWeight: 'bold',
      color: (template.variant === 'modern' || isDark) ? '#FFFFFF' : primary,
    },
    tableRow: {
      flexDirection: 'row',
      padding: 8,
      borderBottomWidth: 1,
      borderBottomColor: isDark ? '#334155' : '#EEEEEE',
    },
    tableCell: {
      fontSize: 10,
      color: text,
    },
    // Columns
    colSn: { width: '5%' },
    colDesc: { width: '40%' },
    colHsn: { width: '10%' },
    colQty: { width: '10%', textAlign: 'center' },
    colRate: { width: '15%', textAlign: 'right' },
    colAmount: { width: '20%', textAlign: 'right' },

    // Totals
    totalsContainer: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      marginBottom: 30,
    },
    totalsTable: {
      width: '40%',
    },
    totalRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: 4,
    },
    totalLabel: {
      fontSize: 10,
      color: secondary,
    },
    totalValue: {
      fontSize: 10,
      textAlign: 'right',
      color: text,
    },
    grandTotalRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: 8,
      borderTopWidth: 1,
      borderTopColor: primary,
      marginTop: 4,
    },
    grandTotalLabel: {
      fontSize: 12,
      fontWeight: 'bold',
      color: primary,
    },
    grandTotalValue: {
      fontSize: 12,
      fontWeight: 'bold',
      color: primary,
    },

    // Footer / Notes
    footerSection: {
      marginTop: 'auto',
      borderTopWidth: 1,
      borderTopColor: '#EEEEEE',
      paddingTop: 10,
    },
    noteText: {
      fontSize: 9,
      color: secondary,
      marginBottom: 4,
    },

    // Watermark
    watermark: {
      position: 'absolute',
      top: '40%',
      left: '30%',
      transform: 'rotate(-45deg)',
      fontSize: 80,
      color: isDark ? '#FFFFFF' : '#000000',
      opacity: 0.05,
      fontWeight: 'bold',
    }
  });
};

// Formatting helper
const formatCurrency = (amount: number, currency: string = 'INR') => {
  const formatted = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    minimumFractionDigits: 2
  }).format(amount || 0);
  // Sanitize for PDF fonts
  return formatted.replace(/₹/g, 'Rs. ');
};


interface InvoicePDFProps {
  data: {
    invoice: Invoice;
    business: BusinessData;
    settings: any;
    template: InvoiceTemplateInterface;
  };
}

const InvoicePDF: React.FC<InvoicePDFProps> = ({ data }) => {
  const { invoice, business, settings, template } = data;
  const styles = useMemo(() => getThemeStyles(template), [template]);

  return (
    <Document>
      <Page size={settings?.paperSize || 'A4'} style={styles.page}>

        {/* Watermark */}
        {(settings?.showWatermark || template.variant === 'futuristic') && (
          <View style={styles.watermark}>
            <Text>{invoice.status === 'paid' ? 'PAID' : 'ORIGINAL'}</Text>
          </View>
        )}

        {/* Header */}
        <View style={styles.headerContainer}>
          <View style={{ width: '60%' }}>
            {/* Logo */}
            {settings?.includeLogo && business?.logoUrl && (
              <Image src={business.logoUrl} style={styles.logo} />
            )}
            <Text style={styles.businessTitle}>
              {business.name || business.businessName || 'Business Name'}
            </Text>
            <Text style={styles.addressText}>{business.address}</Text>
            <Text style={styles.addressText}>{business.city} {business.pincode}</Text>
            {business.gstin && <Text style={styles.addressText}>GSTIN: {business.gstin}</Text>}
            <Text style={styles.addressText}>
              {business.email} {business.phone ? `| ${business.phone}` : ''}
            </Text>
          </View>

          <View style={styles.invoiceTitleBlock}>
            <Text style={styles.invoiceTitle}>{(invoice.status as string) === 'proforma' ? 'PROFORMA' : 'INVOICE'}</Text>
            <View style={styles.metaContainer}>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>Invoice No:</Text>
                <Text style={styles.metaValue}>{invoice.invoiceNumber}</Text>
              </View>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>Date:</Text>
                <Text style={styles.metaValue}>{new Date(invoice.date).toLocaleDateString('en-IN')}</Text>
              </View>
              {settings?.includeDueDate && (
                <View style={styles.metaRow}>
                  <Text style={styles.metaLabel}>Due Date:</Text>
                  <Text style={styles.metaValue}>{new Date(invoice.dueDate).toLocaleDateString('en-IN')}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Bill To */}
        <View style={styles.addressSection}>
          <View style={styles.addressBlock}>
            <Text style={styles.addressLabel}>Bill To</Text>
            <Text style={{ ...styles.addressText, fontWeight: 'bold' }}>{invoice.client}</Text>
            {invoice.clientDetails?.address && <Text style={styles.addressText}>{invoice.clientDetails.address}</Text>}
            {invoice.clientDetails?.gstin && <Text style={styles.addressText}>GSTIN: {invoice.clientDetails.gstin}</Text>}
          </View>

          {(settings?.includePaymentInfo && invoice.bankDetails) && (
            <View style={styles.addressBlock}>
              <Text style={styles.addressLabel}>Bank Details</Text>
              <Text style={styles.addressText}>Bank: {invoice.bankDetails.bankName}</Text>
              <Text style={styles.addressText}>A/c No: {invoice.bankDetails.accountNumber}</Text>
              <Text style={styles.addressText}>IFSC: {invoice.bankDetails.ifscCode}</Text>
            </View>
          )}
        </View>

        {/* Items Table */}
        <View style={styles.tableContainer}>
          {/* Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.colSn]}>#</Text>
            <Text style={[styles.tableHeaderCell, styles.colDesc]}>Item Description</Text>
            <Text style={[styles.tableHeaderCell, styles.colHsn]}>HSN</Text>
            <Text style={[styles.tableHeaderCell, styles.colQty]}>Qty</Text>
            <Text style={[styles.tableHeaderCell, styles.colRate]}>Rate</Text>
            <Text style={[styles.tableHeaderCell, styles.colAmount]}>Amount</Text>
          </View>
          {/* Rows */}
          {invoice.items.map((item, index) => (
            <View key={index} style={styles.tableRow} wrap={false}>
              <Text style={[styles.tableCell, styles.colSn]}>{index + 1}</Text>
              <Text style={[styles.tableCell, styles.colDesc]}>{item.name}</Text>
              <Text style={[styles.tableCell, styles.colHsn]}>{item.hsnCode || '-'}</Text>
              <Text style={[styles.tableCell, styles.colQty]}>{item.quantity}</Text>
              <Text style={[styles.tableCell, styles.colRate]}>{formatCurrency(item.rate)}</Text>
              <Text style={[styles.tableCell, styles.colAmount]}>{formatCurrency(item.amount)}</Text>
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={styles.totalsContainer}>
          <View style={styles.totalsTable}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Subtotal</Text>
              <Text style={styles.totalValue}>{formatCurrency(invoice.subtotal)}</Text>
            </View>
            {invoice.discount > 0 && (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Discount</Text>
                <Text style={styles.totalValue}>-{formatCurrency(invoice.discount)}</Text>
              </View>
            )}
            {invoice.tax > 0 && (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Tax (GST)</Text>
                <Text style={styles.totalValue}>{formatCurrency(invoice.tax)}</Text>
              </View>
            )}
            <View style={styles.grandTotalRow}>
              <Text style={styles.grandTotalLabel}>Total</Text>
              <Text style={styles.grandTotalValue}>{formatCurrency(invoice.amount)}</Text>
            </View>
            <Text style={{ fontSize: 9, fontStyle: 'italic', marginTop: 4, textAlign: 'right', color: '#666' }}>
              (All amounts in INR)
            </Text>
          </View>
        </View>

        {/* Footer / Notes */}
        <View style={styles.footerSection} wrap={false}>
          {invoice.notes && (
            <View style={{ marginBottom: 10 }}>
              <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 2 }}>Notes</Text>
              <Text style={styles.noteText}>{invoice.notes}</Text>
            </View>
          )}
          {invoice.termsAndConditions && (
            <View>
              <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 2 }}>Terms & Conditions</Text>
              <Text style={styles.noteText}>{invoice.termsAndConditions}</Text>
            </View>
          )}
          <Text style={{ fontSize: 8, color: '#999', textAlign: 'center', marginTop: 20 }}>
            This is a computer generated invoice.
          </Text>
        </View>

      </Page>
    </Document>
  );
};

interface InvoicePDFGeneratorProps {
  invoice: Invoice;
  business: BusinessData;
  template?: InvoiceTemplateInterface;
  settings?: any;
  showControls?: boolean;
  previewMode?: boolean;
}

const InvoicePDFGenerator: React.FC<InvoicePDFGeneratorProps> = ({
  invoice,
  business,
  template,
  settings,
  showControls = true,
  previewMode = false
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfReady, setPdfReady] = useState<boolean>(false);
  const pdfViewerRef = useRef<HTMLDivElement>(null);

  // Use default template if none provided
  const activeTemplate = template || {
    id: 1,
    name: 'Default',
    description: 'Default template',
    variant: 'traditional',
    fontFamily: 'Helvetica',
    colorScheme: { primary: '#000', secondary: '#555', background: '#FFF', text: '#000' }
  } as InvoiceTemplateInterface;

  const pdfData = useMemo(() => {
    try {
      if (!invoice) return null;
      return {
        invoice,
        business: business || {} as BusinessData,
        settings: settings || { paperSize: 'A4', showWatermark: false },
        template: activeTemplate
      };
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [invoice, business, settings, activeTemplate]);

  useEffect(() => {
    if (pdfData) {
      setLoading(false);
      setTimeout(() => setPdfReady(true), 500); // Small delay for rendering
    }
  }, [pdfData]);

  const MemoPDFDocument = useCallback(() =>
    pdfData ? <InvoicePDF data={pdfData} /> : <Document><Page></Page></Document>
    , [pdfData]);

  // Handle print
  const handlePrint = useCallback(() => {
    // ... same printing logic ...
    const iframe = pdfViewerRef.current?.querySelector('iframe');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  }, []);

  if (loading || !pdfData) {
    return <Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>;
  }

  return (
    <Paper elevation={0} sx={{ p: previewMode ? 0 : 3, borderRadius: 2 }}>
      {showControls && (
        <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Preview ({activeTemplate.name})</Typography>
          <Box display="flex" gap={1}>
            <PDFDownloadLink
              document={<MemoPDFDocument />}
              fileName={`Invoice_${invoice.invoiceNumber}.pdf`}
              style={{ textDecoration: 'none' }}
            >
              {({ loading: pdfLoading }) => (
                <Button variant="contained" disabled={pdfLoading} startIcon={<DownloadIcon />}>
                  {pdfLoading ? 'Loading...' : 'Download'}
                </Button>
              )}
            </PDFDownloadLink>
            <IconButton onClick={handlePrint}><PrintIcon /></IconButton>
          </Box>
        </Box>
      )}

      {!pdfReady ? (
        <Skeleton variant="rectangular" height={600} />
      ) : (
        <Box ref={pdfViewerRef}>
          <PDFViewer width="100%" height={previewMode ? "70vh" : 600} style={{ border: 'none' }}>
            <MemoPDFDocument />
          </PDFViewer>
        </Box>
      )}
    </Paper>
  );
};

export default React.memo(InvoicePDFGenerator);